import React, { createContext, useEffect, useState } from "react";
import s from "./CameraZonesView.less";
import { useTranslation } from "react-i18next";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import Button from "~/shared/ui/Fields/Button";
import Add from "~/components/Icons/icon-add";
import ZoneListPart from "~/pages/CameraZonesView/parts/ZoneListPart";
import { useHistory, useParams } from "react-router-dom";
import ZoneViewPart from "~/pages/CameraZonesView/parts/ZoneViewPart";
import Back from "~/components/Icons/arrow-prev2";
import {
  CameraZone,
  Dealer,
  Exact,
  GetCameraListQuery,
  useGetCameraListLazyQuery,
  useGetCameraZoneByIdLazyQuery,
  useGetCameraZonesLazyQuery,
} from "~/schemas/generated/graphql";
import { get } from "lodash";
import CameraModal from "~/pages/CameraZonesView/modals/CameraModal";
import CameraViewModal from "~/pages/CameraZonesView/modals/CameraViewModal";
import { useCurrentDealerInfo } from "~/hooks/useCurrentDealerInfo";
import { LazyQueryResult } from "@apollo/react-hooks";
import DeleteCameraModal from "~/pages/CameraZonesView/modals/DeleteCameraModal";
import { usePermissions } from "~/shared/context/PermissionContext";
import { hasPermissionForNameWithTypeCheck } from "~/pages/Ivideon/lib/hasPermissionForNameWithTypeCheck";
import { hasGetGroupsPermissionForNameWithTypeCheck } from "~/pages/Ivideon/lib/hasGetGroupsPermissionForNameWithTypeCheck";

interface ICamerasContext {
  fetchCameras: () => void;
  fetchZonesHandler: () => void;
  userCanAddCamera?: boolean;
  userCanManageCamera?: boolean;
  userCanAuditCamera?: boolean;
  getCameraListState?: LazyQueryResult<
    GetCameraListQuery,
    Exact<{
      count?: number;
      page?: number;
      cameraZoneId?: string;
      dealerId?: string;
    }>
  >;
  dealerInfo?: Dealer;
  dealerInfoLoading?: boolean;
}

export const CamerasContext = createContext<ICamerasContext>({
  fetchCameras: () => {},
  fetchZonesHandler: () => {},
});

type TParameters = {
  page: string;
  count: string;
  openModal: string;
};

const CameraZonesView = () => {
  // Utils
  const { t } = useTranslation();
  const setURLParams = useURLParamsSetter();
  const { page, count = "25", openModal } = useURLParamsGetter<TParameters>();
  const params = useParams<{ id: string }>();
  const { id } = params;
  const history = useHistory();
  const [dealerInfo, dealerInfoLoading] = useCurrentDealerInfo();

  // Access model
  const { hasPermission } = usePermissions();
  const userCanAddCamera =
    hasPermission("ivideonGroups.acceptGroup.sale") ||
    hasPermission("ivideonGroups.acceptGroup.service");

  // State
  const [zones, setZones] = useState<CameraZone[]>([]);
  const [zonesTotal, setZonesTotal] = useState(0);
  const [currentZone, setCurrentZone] = useState<CameraZone | undefined>();

  const currentZoneName = currentZone?.name ?? "";
  const userCanManageCamera = hasPermissionForNameWithTypeCheck(
    currentZoneName,
    hasPermission
  );
  const userCanAuditCamera =
    userCanManageCamera ||
    hasGetGroupsPermissionForNameWithTypeCheck(currentZoneName, hasPermission);

  // Query
  const [getCurrentZone, getCurrentZoneState] = useGetCameraZoneByIdLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [getZones, getZonesState] = useGetCameraZonesLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [getCameraList, getCameraListState] = useGetCameraListLazyQuery({
    fetchPolicy: "no-cache",
  });

  const goBack = () => {
    history.push(`/camera-view/`);
    setCurrentZone(undefined);
  };
  const fetchCameras = () =>
    getCameraList({
      variables: {
        page: Number(page),
        count: Number(count),
        dialerId: dealerInfo.id,
        cameraZoneId: currentZone.id,
      },
    });
  const toggleCreteModal = () =>
    openModal
      ? setURLParams({ openModal: undefined, currentId: undefined })
      : setURLParams({ openModal: "Create" });

  const title = id
    ? `${t("cameraView.zoneViewTitle")} ${
        !currentZone ? "..." : currentZone.name
      }`
    : t("cameraView.zonesListTitle");

  const fetchZonesHandler = () => {
    getZones({
      variables: {
        dealerId: dealerInfo.id,
        page: Number(page),
        count: Number(count),
      },
    });
  };

  useEffect(() => {
    if (dealerInfo && !dealerInfoLoading) fetchZonesHandler();
  }, [dealerInfoLoading]);

  useEffect(() => {
    if (!getZonesState.loading && getZonesState.data) {
      setZones(
        get(getZonesState.data, "CameraGetter.getCameraZones.items", []).filter(
          (zone: CameraZone) =>
            hasGetGroupsPermissionForNameWithTypeCheck(zone.name, hasPermission)
        )
      );
      setZonesTotal(
        get(getZonesState.data, "CameraGetter.getCameraZones.total", 0)
      );
    }
  }, [getZonesState.loading, hasPermission]);

  useEffect(() => {
    if (!!id) getCurrentZone({ variables: { id } });
  }, [id]);

  useEffect(() => {
    if (!getCurrentZoneState.loading && getCurrentZoneState.data) {
      setCurrentZone(
        get(getCurrentZoneState.data, "CameraGetter.getCameraZone")
      );
    }
  }, [getCurrentZoneState.loading]);

  return (
    <CamerasContext.Provider
      value={{
        fetchCameras,
        fetchZonesHandler,
        getCameraListState,
        dealerInfo,
        dealerInfoLoading,
        userCanAddCamera,
        userCanManageCamera,
        userCanAuditCamera,
      }}
    >
      <div className={s.wrapper}>
        <div className={s.title}>{title}</div>
        {id && (
          <Button txtBtn={t("ui.back")} onClick={goBack} small>
            <Back />
          </Button>
        )}
        {userCanAddCamera && (
          <Button
            txtBtn={t("cameraView.add")}
            onClick={toggleCreteModal}
            small
            orange
            withMargin
          >
            <Add />
          </Button>
        )}
        <Button txtBtn={"IVIDEON"} onClick={toggleCreteModal} small />
        {id ? (
          <ZoneViewPart currentZone={currentZone} />
        ) : (
          <ZoneListPart
            zones={zones}
            zonesTotal={zonesTotal}
            loading={getZonesState.loading}
          />
        )}
        <CameraModal />
        <DeleteCameraModal />
        {openModal === "ViewCamera" && <CameraViewModal />}
      </div>
    </CamerasContext.Provider>
  );
};

export default CameraZonesView;
